import React, { useState, useEffect } from 'react';
import {
  X, Upload, Shield, Zap, Lock, Server, Key, Globe, Wifi,
  ChevronRight, ChevronLeft, Check, AlertCircle, Eye, EyeOff,
  FileText, Loader2, Network
} from 'lucide-react';
import { toast } from '../../utils/toastUtils';
import { vpn, workspaces as workspacesApi } from '../../services/api/client';

const VPN_PROTOCOLS = {
  openvpn: {
    name: 'OpenVPN',
    icon: Shield,
    description: 'Protocole VPN open source fiable et securise',
    color: 'text-orange-400',
    bgColor: 'bg-orange-500/10',
    borderColor: 'border-orange-500/30',
    fileExt: '.ovpn',
    features: ['Compatible tous OS', 'Fichier .ovpn', 'Authentification flexible']
  },
  wireguard: {
    name: 'WireGuard',
    icon: Zap,
    description: 'VPN moderne, rapide et leger',
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/10',
    borderColor: 'border-blue-500/30',
    fileExt: '.conf',
    features: ['Ultra rapide', 'Configuration simple', 'Faible latence']
  },
  l2tp: {
    name: 'L2TP/IPSec',
    icon: Lock,
    description: 'Compatible Windows natif, sécurité enterprise',
    color: 'text-purple-400',
    bgColor: 'bg-purple-500/10',
    borderColor: 'border-purple-500/30',
    fileExt: null,
    features: ['Windows natif', 'Pre-shared Key', 'Compatible enterprise']
  }
};

const VPNConfigWizard = ({ isOpen, onClose, workspaceId, workspaceName, onSuccess }) => {
  // Steps: 1 = Protocol Selection, 2 = Configuration, 3 = Test & Confirm
  const [step, setStep] = useState(1);
  const [selectedProtocol, setSelectedProtocol] = useState(null);
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // Existing VPN configs
  const [existingConfigs, setExistingConfigs] = useState({ externe: [], interne: [] });
  const [loadingConfigs, setLoadingConfigs] = useState(false);
  const [selectedExistingConfig, setSelectedExistingConfig] = useState(null);

  // Form data
  const [configMode, setConfigMode] = useState('file'); // 'file', 'manual', 'existing'
  const [vpnName, setVpnName] = useState('');
  const [vpnFile, setVpnFile] = useState(null);
  const [showPasswords, setShowPasswords] = useState({});

  // OpenVPN specific
  const [openvpnCredentials, setOpenvpnCredentials] = useState({ username: '', password: '' });

  // WireGuard specific
  const [wireguardConfig, setWireguardConfig] = useState({
    endpoint: '',
    public_key: '',
    private_key: '',
    address: '',
    dns: '1.1.1.1',
    preshared_key: '',
    allowed_ips: '0.0.0.0/0'
  });

  // L2TP specific
  const [l2tpConfig, setL2tpConfig] = useState({
    server: '',
    psk: '',
    username: '',
    password: ''
  });

  // Fetch existing VPN configs
  useEffect(() => {
    if (isOpen && workspaceId) {
      fetchExistingConfigs();
    }
  }, [isOpen, workspaceId]);

  const fetchExistingConfigs = async () => {
    try {
      setLoadingConfigs(true);
      // Get external (shared) configs
      const externeResponse = await vpn.getConfigs('externe', null);
      // Get internal (workspace-specific) configs
      const interneResponse = await vpn.getConfigs('interne', workspaceId);

      // Extract configs from response - API returns { externe: [...] } or { interne: [...] }
      const externeConfigs = externeResponse?.externe || externeResponse || [];
      const interneConfigs = interneResponse?.interne || interneResponse || [];

      setExistingConfigs({
        externe: Array.isArray(externeConfigs) ? externeConfigs : [],
        interne: Array.isArray(interneConfigs) ? interneConfigs : []
      });
    } catch (error) {
      console.error('Error fetching VPN configs:', error);
    } finally {
      setLoadingConfigs(false);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const expectedExt = VPN_PROTOCOLS[selectedProtocol]?.fileExt;
    if (expectedExt && !file.name.endsWith(expectedExt)) {
      toast.error(`Fichier invalide. Attendu: ${expectedExt}`);
      return;
    }

    setVpnFile(file);
    if (!vpnName) {
      setVpnName(file.name.replace(/\.(ovpn|conf)$/, ''));
    }
  };

  const togglePassword = (field) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleProtocolSelect = (protocol) => {
    setSelectedProtocol(protocol);
    setConfigMode(protocol === 'l2tp' ? 'manual' : 'file');
    setTestResult(null);
  };

  const canProceedStep2 = () => {
    if (!selectedProtocol) return false;

    if (configMode === 'existing') {
      return !!selectedExistingConfig;
    }

    if (configMode === 'file') {
      return !!vpnFile && !!vpnName;
    }

    if (configMode === 'manual') {
      if (selectedProtocol === 'wireguard') {
        return wireguardConfig.endpoint && wireguardConfig.public_key &&
               wireguardConfig.private_key && wireguardConfig.address && vpnName;
      }
      if (selectedProtocol === 'l2tp') {
        return l2tpConfig.server && l2tpConfig.psk &&
               l2tpConfig.username && l2tpConfig.password && vpnName;
      }
    }

    return false;
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);

    try {
      // Simulate test - in real implementation, call backend API
      await new Promise(resolve => setTimeout(resolve, 2000));

      setTestResult({
        success: true,
        message: 'Configuration VPN valide',
        details: {
          protocol: selectedProtocol,
          server: configMode === 'manual'
            ? (selectedProtocol === 'wireguard' ? wireguardConfig.endpoint : l2tpConfig.server)
            : 'Défini dans le fichier'
        }
      });
    } catch (error) {
      setTestResult({
        success: false,
        message: 'Erreur de connexion',
        error: error.message
      });
    } finally {
      setTesting(false);
    }
  };

  const handleSaveConfig = async () => {
    setLoading(true);

    console.log('[VPN Save] Starting save...', { configMode, selectedProtocol, vpnFile: vpnFile?.name, workspaceId });

    try {
      let configData = {};
      let credentials = null;

      if (configMode === 'existing') {
        // Use existing config - update workspace with vpn_config_id
        console.log('[VPN Save] Using existing config:', selectedExistingConfig?.id);
        await workspacesApi.update(workspaceId, {
          vpn_config_id: selectedExistingConfig.id
        });
        toast.success('Configuration VPN assignee avec succes');
        onSuccess?.();
        onClose();
        return;
      }

      if (configMode === 'file' && vpnFile) {
        // Upload file using multi-protocol VPN endpoint (supports .ovpn, .conf, .json)
        console.log('[VPN Save] Uploading file:', vpnFile.name, 'protocol:', selectedProtocol);
        const uploadResult = await vpn.uploadMultiProtocol(vpnFile, workspaceId, selectedProtocol);
        console.log('[VPN Save] Upload result:', uploadResult);

        // If OpenVPN with credentials, save them
        if (selectedProtocol === 'openvpn' && openvpnCredentials.username && uploadResult?.config_id) {
          await vpn.saveCredentials(
            uploadResult.config_id,
            openvpnCredentials.username,
            openvpnCredentials.password,
            workspaceId
          );
        }
      } else if (configMode === 'manual') {
        // For manual config, create config file and upload via multi-protocol endpoint

        if (selectedProtocol === 'wireguard') {
          // Create WireGuard config content
          const wgConfig = `[Interface]
PrivateKey = ${wireguardConfig.private_key}
Address = ${wireguardConfig.address}
DNS = ${wireguardConfig.dns}

[Peer]
PublicKey = ${wireguardConfig.public_key}
${wireguardConfig.preshared_key ? `PresharedKey = ${wireguardConfig.preshared_key}` : ''}
Endpoint = ${wireguardConfig.endpoint}
AllowedIPs = ${wireguardConfig.allowed_ips}
PersistentKeepalive = 25`;

          // Create a file blob and upload via multi-protocol endpoint
          const blob = new Blob([wgConfig], { type: 'text/plain' });
          const file = new File([blob], `${vpnName}.conf`, { type: 'text/plain' });

          await vpn.uploadMultiProtocol(file, workspaceId, 'wireguard');
        } else if (selectedProtocol === 'l2tp') {
          // L2TP config as JSON file
          const l2tpJsonConfig = {
            server: l2tpConfig.server,
            psk: l2tpConfig.psk,
            username: l2tpConfig.username,
            password: l2tpConfig.password
          };

          // Create JSON file and upload via multi-protocol endpoint
          const blob = new Blob([JSON.stringify(l2tpJsonConfig, null, 2)], { type: 'application/json' });
          const file = new File([blob], `${vpnName}.json`, { type: 'application/json' });

          await vpn.uploadMultiProtocol(file, workspaceId, 'l2tp');
        }
      }

      toast.success('Configuration VPN enregistree avec succes');
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('[VPN Save] ERROR:', error);
      console.error('[VPN Save] Error response:', error.response);
      console.error('[VPN Save] Error message:', error.message);
      toast.error(error.response?.data?.detail || error.message || 'Erreur lors de l\'enregistrement');
    } finally {
      setLoading(false);
    }
  };

  const resetWizard = () => {
    setStep(1);
    setSelectedProtocol(null);
    setConfigMode('file');
    setVpnName('');
    setVpnFile(null);
    setSelectedExistingConfig(null);
    setTestResult(null);
    setOpenvpnCredentials({ username: '', password: '' });
    setWireguardConfig({
      endpoint: '',
      public_key: '',
      private_key: '',
      address: '',
      dns: '1.1.1.1',
      preshared_key: '',
      allowed_ips: '0.0.0.0/0'
    });
    setL2tpConfig({
      server: '',
      psk: '',
      username: '',
      password: ''
    });
  };

  useEffect(() => {
    if (!isOpen) {
      resetWizard();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const allExistingConfigs = [
    ...(Array.isArray(existingConfigs?.externe) ? existingConfigs.externe : []),
    ...(Array.isArray(existingConfigs?.interne) ? existingConfigs.interne : [])
  ];

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-brand-black-light border border-brand-green/30 rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-800">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Network className="w-6 h-6 text-brand-green" />
                Configuration VPN
              </h2>
              <p className="text-gray-400 text-sm mt-1">
                Workspace: <span className="text-white">{workspaceName}</span>
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-center gap-4 mt-6">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                  step >= s
                    ? 'bg-brand-green text-black'
                    : 'bg-gray-800 text-gray-500'
                }`}>
                  {step > s ? <Check className="w-4 h-4" /> : s}
                </div>
                {s < 3 && (
                  <div className={`w-16 h-0.5 mx-2 ${
                    step > s ? 'bg-brand-green' : 'bg-gray-800'
                  }`} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-center gap-8 mt-2 text-xs text-gray-500">
            <span className={step >= 1 ? 'text-brand-green' : ''}>Protocole</span>
            <span className={step >= 2 ? 'text-brand-green' : ''}>Configuration</span>
            <span className={step >= 3 ? 'text-brand-green' : ''}>Validation</span>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Step 1: Protocol Selection */}
          {step === 1 && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-white mb-4">
                Choisissez votre protocole VPN
              </h3>

              <div className="grid gap-4">
                {Object.entries(VPN_PROTOCOLS).map(([key, protocol]) => {
                  const Icon = protocol.icon;
                  const isSelected = selectedProtocol === key;

                  return (
                    <button
                      key={key}
                      onClick={() => handleProtocolSelect(key)}
                      className={`p-4 rounded-xl border-2 transition-all text-left ${
                        isSelected
                          ? `${protocol.borderColor} ${protocol.bgColor}`
                          : 'border-gray-800 hover:border-gray-700 bg-brand-black/50'
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`p-3 rounded-lg ${protocol.bgColor}`}>
                          <Icon className={`w-6 h-6 ${protocol.color}`} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="text-white font-semibold">{protocol.name}</h4>
                            {isSelected && (
                              <Check className="w-4 h-4 text-brand-green" />
                            )}
                          </div>
                          <p className="text-gray-400 text-sm mt-1">{protocol.description}</p>
                          <div className="flex flex-wrap gap-2 mt-3">
                            {protocol.features.map((feature, idx) => (
                              <span
                                key={idx}
                                className="px-2 py-1 bg-gray-800 text-gray-300 text-xs rounded"
                              >
                                {feature}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 2: Configuration */}
          {step === 2 && selectedProtocol && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-white mb-4">
                Configuration {VPN_PROTOCOLS[selectedProtocol].name}
              </h3>

              {/* Config Mode Selector */}
              {selectedProtocol !== 'l2tp' && (
                <div className="flex gap-2 p-1 bg-gray-900 rounded-lg mb-6">
                  {allExistingConfigs.length > 0 && (
                    <button
                      onClick={() => setConfigMode('existing')}
                      className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                        configMode === 'existing'
                          ? 'bg-brand-green text-black'
                          : 'text-gray-400 hover:text-white'
                      }`}
                    >
                      Configuration existante
                    </button>
                  )}
                  <button
                    onClick={() => setConfigMode('file')}
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                      configMode === 'file'
                        ? 'bg-brand-green text-black'
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    Importer un fichier
                  </button>
                  {selectedProtocol === 'wireguard' && (
                    <button
                      onClick={() => setConfigMode('manual')}
                      className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                        configMode === 'manual'
                          ? 'bg-brand-green text-black'
                          : 'text-gray-400 hover:text-white'
                      }`}
                    >
                      Configuration manuelle
                    </button>
                  )}
                </div>
              )}

              {/* Existing Config Selection */}
              {configMode === 'existing' && (
                <div className="space-y-4">
                  {loadingConfigs ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-brand-green" />
                    </div>
                  ) : (
                    <>
                      {existingConfigs.externe.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-400 mb-2">VPN Externes (partages)</h4>
                          <div className="space-y-2">
                            {existingConfigs.externe.map((config) => (
                              <button
                                key={config.id}
                                onClick={() => setSelectedExistingConfig(config)}
                                className={`w-full p-3 rounded-lg border text-left transition-all ${
                                  selectedExistingConfig?.id === config.id
                                    ? 'border-brand-green bg-brand-green/10'
                                    : 'border-gray-800 hover:border-gray-700'
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="text-white font-medium">{config.name}</p>
                                    <p className="text-gray-500 text-sm">
                                      {config.remote_host}:{config.remote_port}
                                    </p>
                                  </div>
                                  {selectedExistingConfig?.id === config.id && (
                                    <Check className="w-5 h-5 text-brand-green" />
                                  )}
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {existingConfigs.interne.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-400 mb-2">VPN Internes (workspace)</h4>
                          <div className="space-y-2">
                            {existingConfigs.interne.map((config) => (
                              <button
                                key={config.id}
                                onClick={() => setSelectedExistingConfig(config)}
                                className={`w-full p-3 rounded-lg border text-left transition-all ${
                                  selectedExistingConfig?.id === config.id
                                    ? 'border-brand-green bg-brand-green/10'
                                    : 'border-gray-800 hover:border-gray-700'
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="text-white font-medium">{config.name}</p>
                                    <p className="text-gray-500 text-sm">
                                      {config.remote_host}:{config.remote_port}
                                    </p>
                                  </div>
                                  {selectedExistingConfig?.id === config.id && (
                                    <Check className="w-5 h-5 text-brand-green" />
                                  )}
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* File Upload */}
              {configMode === 'file' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Nom de la configuration
                    </label>
                    <input
                      type="text"
                      value={vpnName}
                      onChange={(e) => setVpnName(e.target.value)}
                      placeholder="Ex: VPN Entreprise"
                      className="w-full px-4 py-3 bg-brand-black border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-brand-green"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Fichier de configuration ({VPN_PROTOCOLS[selectedProtocol].fileExt})
                    </label>
                    <div className="border-2 border-dashed border-gray-700 rounded-lg p-6 text-center hover:border-brand-green/50 transition-colors">
                      <input
                        type="file"
                        accept={VPN_PROTOCOLS[selectedProtocol].fileExt}
                        onChange={handleFileUpload}
                        className="hidden"
                        id="vpn-file-upload"
                      />
                      <label htmlFor="vpn-file-upload" className="cursor-pointer">
                        {vpnFile ? (
                          <div className="flex items-center justify-center gap-2">
                            <FileText className="w-8 h-8 text-brand-green" />
                            <span className="text-white">{vpnFile.name}</span>
                          </div>
                        ) : (
                          <>
                            <Upload className="w-10 h-10 text-gray-500 mx-auto mb-2" />
                            <p className="text-gray-400">
                              Cliquez pour selectionner un fichier {VPN_PROTOCOLS[selectedProtocol].fileExt}
                            </p>
                          </>
                        )}
                      </label>
                    </div>
                  </div>

                  {/* OpenVPN Credentials */}
                  {selectedProtocol === 'openvpn' && (
                    <div className="p-4 bg-gray-900/50 rounded-lg space-y-4">
                      <h4 className="text-sm font-medium text-gray-300 flex items-center gap-2">
                        <Key className="w-4 h-4" />
                        Identifiants (optionnel)
                      </h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Nom d'utilisateur</label>
                          <input
                            type="text"
                            value={openvpnCredentials.username}
                            onChange={(e) => setOpenvpnCredentials(prev => ({ ...prev, username: e.target.value }))}
                            className="w-full px-3 py-2 bg-brand-black border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-brand-green"
                          />
                        </div>
                        <div className="relative">
                          <label className="block text-xs text-gray-500 mb-1">Mot de passe</label>
                          <input
                            type={showPasswords.openvpn ? 'text' : 'password'}
                            value={openvpnCredentials.password}
                            onChange={(e) => setOpenvpnCredentials(prev => ({ ...prev, password: e.target.value }))}
                            className="w-full px-3 py-2 pr-10 bg-brand-black border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-brand-green"
                          />
                          <button
                            type="button"
                            onClick={() => togglePassword('openvpn')}
                            className="absolute right-2 top-7 text-gray-500 hover:text-gray-300"
                          >
                            {showPasswords.openvpn ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* WireGuard Manual Config */}
              {configMode === 'manual' && selectedProtocol === 'wireguard' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Nom de la configuration
                    </label>
                    <input
                      type="text"
                      value={vpnName}
                      onChange={(e) => setVpnName(e.target.value)}
                      placeholder="Ex: WireGuard Entreprise"
                      className="w-full px-4 py-3 bg-brand-black border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-brand-green"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">
                        Endpoint (serveur:port)&nbsp;<span className="text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        value={wireguardConfig.endpoint}
                        onChange={(e) => setWireguardConfig(prev => ({ ...prev, endpoint: e.target.value }))}
                        placeholder="vpn.exemple.com:51820"
                        className="w-full px-3 py-2 bg-brand-black border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-brand-green"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">
                        Adresse IP client&nbsp;<span className="text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        value={wireguardConfig.address}
                        onChange={(e) => setWireguardConfig(prev => ({ ...prev, address: e.target.value }))}
                        placeholder="10.0.0.2/24"
                        className="w-full px-3 py-2 bg-brand-black border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-brand-green"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      Cle publique du serveur&nbsp;<span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      value={wireguardConfig.public_key}
                      onChange={(e) => setWireguardConfig(prev => ({ ...prev, public_key: e.target.value }))}
                      placeholder="Clé publique en Base64"
                      className="w-full px-3 py-2 bg-brand-black border border-gray-700 rounded-lg text-white text-sm font-mono focus:outline-none focus:border-brand-green"
                    />
                  </div>

                  <div className="relative">
                    <label className="block text-xs text-gray-500 mb-1">
                      Cle privee client&nbsp;<span className="text-red-400">*</span>
                    </label>
                    <input
                      type={showPasswords.wgPrivate ? 'text' : 'password'}
                      value={wireguardConfig.private_key}
                      onChange={(e) => setWireguardConfig(prev => ({ ...prev, private_key: e.target.value }))}
                      placeholder="Clé privée en Base64"
                      className="w-full px-3 py-2 pr-10 bg-brand-black border border-gray-700 rounded-lg text-white text-sm font-mono focus:outline-none focus:border-brand-green"
                    />
                    <button
                      type="button"
                      onClick={() => togglePassword('wgPrivate')}
                      className="absolute right-2 top-7 text-gray-500 hover:text-gray-300"
                    >
                      {showPasswords.wgPrivate ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">DNS</label>
                      <input
                        type="text"
                        value={wireguardConfig.dns}
                        onChange={(e) => setWireguardConfig(prev => ({ ...prev, dns: e.target.value }))}
                        placeholder="1.1.1.1"
                        className="w-full px-3 py-2 bg-brand-black border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-brand-green"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Allowed IPs</label>
                      <input
                        type="text"
                        value={wireguardConfig.allowed_ips}
                        onChange={(e) => setWireguardConfig(prev => ({ ...prev, allowed_ips: e.target.value }))}
                        placeholder="0.0.0.0/0"
                        className="w-full px-3 py-2 bg-brand-black border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-brand-green"
                      />
                    </div>
                  </div>

                  <div className="relative">
                    <label className="block text-xs text-gray-500 mb-1">Pre-shared Key (optionnel)</label>
                    <input
                      type={showPasswords.wgPsk ? 'text' : 'password'}
                      value={wireguardConfig.preshared_key}
                      onChange={(e) => setWireguardConfig(prev => ({ ...prev, preshared_key: e.target.value }))}
                      placeholder="Clé PSK en Base64"
                      className="w-full px-3 py-2 pr-10 bg-brand-black border border-gray-700 rounded-lg text-white text-sm font-mono focus:outline-none focus:border-brand-green"
                    />
                    <button
                      type="button"
                      onClick={() => togglePassword('wgPsk')}
                      className="absolute right-2 top-7 text-gray-500 hover:text-gray-300"
                    >
                      {showPasswords.wgPsk ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              )}

              {/* L2TP Manual Config */}
              {(configMode === 'manual' || selectedProtocol === 'l2tp') && selectedProtocol === 'l2tp' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Nom de la configuration
                    </label>
                    <input
                      type="text"
                      value={vpnName}
                      onChange={(e) => setVpnName(e.target.value)}
                      placeholder="Ex: L2TP Entreprise"
                      className="w-full px-4 py-3 bg-brand-black border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-brand-green"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">
                        Adresse du serveur&nbsp;<span className="text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        value={l2tpConfig.server}
                        onChange={(e) => setL2tpConfig(prev => ({ ...prev, server: e.target.value }))}
                        placeholder="vpn.exemple.com"
                        className="w-full px-3 py-2 bg-brand-black border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-brand-green"
                      />
                    </div>
                    <div className="relative">
                      <label className="block text-xs text-gray-500 mb-1">
                        Pre-Shared Key (PSK)&nbsp;<span className="text-red-400">*</span>
                      </label>
                      <input
                        type={showPasswords.l2tpPsk ? 'text' : 'password'}
                        value={l2tpConfig.psk}
                        onChange={(e) => setL2tpConfig(prev => ({ ...prev, psk: e.target.value }))}
                        placeholder="Cle partagee IPSec"
                        className="w-full px-3 py-2 pr-10 bg-brand-black border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-brand-green"
                      />
                      <button
                        type="button"
                        onClick={() => togglePassword('l2tpPsk')}
                        className="absolute right-2 top-7 text-gray-500 hover:text-gray-300"
                      >
                        {showPasswords.l2tpPsk ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="p-4 bg-gray-900/50 rounded-lg space-y-4">
                    <h4 className="text-sm font-medium text-gray-300 flex items-center gap-2">
                      <Key className="w-4 h-4" />
                      Identifiants de connexion
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">
                          Nom d'utilisateur&nbsp;<span className="text-red-400">*</span>
                        </label>
                        <input
                          type="text"
                          value={l2tpConfig.username}
                          onChange={(e) => setL2tpConfig(prev => ({ ...prev, username: e.target.value }))}
                          className="w-full px-3 py-2 bg-brand-black border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-brand-green"
                        />
                      </div>
                      <div className="relative">
                        <label className="block text-xs text-gray-500 mb-1">
                          Mot de passe&nbsp;<span className="text-red-400">*</span>
                        </label>
                        <input
                          type={showPasswords.l2tpPass ? 'text' : 'password'}
                          value={l2tpConfig.password}
                          onChange={(e) => setL2tpConfig(prev => ({ ...prev, password: e.target.value }))}
                          className="w-full px-3 py-2 pr-10 bg-brand-black border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-brand-green"
                        />
                        <button
                          type="button"
                          onClick={() => togglePassword('l2tpPass')}
                          className="absolute right-2 top-7 text-gray-500 hover:text-gray-300"
                        >
                          {showPasswords.l2tpPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Test & Confirm */}
          {step === 3 && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-white mb-4">
                Validation de la configuration
              </h3>

              {/* Summary */}
              <div className="p-4 bg-gray-900/50 rounded-lg space-y-3">
                <h4 className="text-sm font-medium text-gray-300">Résumé de la configuration</h4>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Protocole:</span>
                    <span className="text-white ml-2">{VPN_PROTOCOLS[selectedProtocol]?.name}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Mode:</span>
                    <span className="text-white ml-2">
                      {configMode === 'existing' ? 'Configuration existante' :
                       configMode === 'file' ? 'Fichier importe' : 'Configuration manuelle'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Nom:</span>
                    <span className="text-white ml-2">
                      {configMode === 'existing' ? selectedExistingConfig?.name : vpnName}
                    </span>
                  </div>
                  {configMode === 'file' && vpnFile && (
                    <div>
                      <span className="text-gray-500">Fichier:</span>
                      <span className="text-white ml-2">{vpnFile.name}</span>
                    </div>
                  )}
                  {configMode === 'manual' && selectedProtocol === 'wireguard' && (
                    <div>
                      <span className="text-gray-500">Endpoint:</span>
                      <span className="text-white ml-2">{wireguardConfig.endpoint}</span>
                    </div>
                  )}
                  {selectedProtocol === 'l2tp' && (
                    <div>
                      <span className="text-gray-500">Serveur:</span>
                      <span className="text-white ml-2">{l2tpConfig.server}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Test Result */}
              {testResult && (
                <div className={`p-4 rounded-lg border ${
                  testResult.success
                    ? 'bg-green-500/10 border-green-500/30'
                    : 'bg-red-500/10 border-red-500/30'
                }`}>
                  <div className="flex items-center gap-2">
                    {testResult.success ? (
                      <Check className="w-5 h-5 text-green-400" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-red-400" />
                    )}
                    <span className={testResult.success ? 'text-green-400' : 'text-red-400'}>
                      {testResult.message}
                    </span>
                  </div>
                  {testResult.error && (
                    <p className="text-red-300 text-sm mt-2">{testResult.error}</p>
                  )}
                </div>
              )}

              {/* Test Button */}
              <button
                onClick={handleTestConnection}
                disabled={testing}
                className="w-full py-3 border border-brand-green text-brand-green rounded-lg hover:bg-brand-green/10 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {testing ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Test en cours...
                  </>
                ) : (
                  <>
                    <Wifi className="w-5 h-5" />
                    Tester la configuration
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-800 flex items-center justify-between">
          <button
            onClick={() => step > 1 ? setStep(step - 1) : onClose()}
            className="px-4 py-2 text-gray-400 hover:text-white transition-colors flex items-center gap-2"
          >
            <ChevronLeft className="w-4 h-4" />
            {step > 1 ? 'Retour' : 'Annuler'}
          </button>

          <div className="flex items-center gap-3">
            {step < 3 ? (
              <button
                onClick={() => setStep(step + 1)}
                disabled={step === 1 ? !selectedProtocol : !canProceedStep2()}
                className="px-6 py-2 bg-brand-green text-black rounded-lg hover:bg-brand-green/90 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Suivant
                <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={handleSaveConfig}
                disabled={loading}
                className="px-6 py-2 bg-brand-green text-black rounded-lg hover:bg-brand-green/90 transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Enregistrer
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VPNConfigWizard;
